import { Brand, DeckPrint, DeckViewer, Frame, Img, Nav } from "./DeckShell";
import { CinematicCover, CINE_ASSETS } from "./CinematicCover";
import { LogoStamp, Wordmark } from "./brands";
import { Proposal } from "./Proposal";
import { WhatItIs } from "./WhatItIs";
import "../buildpartner/build.css";
import "../lawpartner/law.css";

/* Headliner Partnership deck — slide-native 16:9. Masterdeck + tynt mottakerlag (?for=&category=). Skall i DeckShell. */

/* 01 Cover — felles kinematisk cover (CinematicCover): Grieghallen full-bleed, Kevin (proposed) i høyre tredjedel, co-brand-lockup.
   Tittel etter brukerens review: «A room for the people deciding what comes next.» — ikke «where Norway decides» (selvutnevnt før første utgave). */
const Cover = ({ mottaker }) => (
  <CinematicCover
    lockup={<p className="bd-lockup" data-testid="hd-wordmark"><Brand size={19} /><i>×</i><Wordmark name={mottaker || "Headliner Partner"} h={24} /></p>}
    label={<><b>Headliner Partnership</b>Private &amp; confidential</>}
    className="vd-cover-tight"
    title={["A room for the people", "deciding what comes next."]}
    sub={<>One room, 700 of Norway's leaders — with <b>Kevin O'Leary</b> proposed to open the day{mottaker ? <>, and <b>{mottaker}</b> in the commercial leadership position.</> : ", and one partner in the commercial leadership position."}</>}
    index={[
      ["Date", "13 April 2027"],
      ["Venue", "Grieghallen, Bergen"],
      [mottaker ? "Prepared for" : "Audience", mottaker || "700 seats · invitation-led", mottaker ? "prep" : "", "hd-prepared"],
    ]}
  />
);

/* 02 The ambition — statement + «rommet» som data-illustrasjon: nøyaktig 700 punkter (28×25), ett punkt per beslutningstaker. */
export const Room700 = () => {
  const cols = 28, rows = 25, gap = 20;
  const dots = [];
  for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) {
    dots.push(<circle key={`${i}-${j}`} cx={i * gap + 3} cy={j * gap + 3} r="2.6" style={{ animationDelay: `${0.5 + j * 0.028 + i * 0.006}s` }} />);
  }
  return (
    <svg className="hd-room700" viewBox={`0 0 ${(cols - 1) * gap + 6} ${(rows - 1) * gap + 6}`} width={(cols - 1) * gap + 6} height={(rows - 1) * gap + 6} fill="currentColor" aria-hidden data-testid="hd-room700">
      {dots}
    </svg>
  );
};

export const Ambition = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The ambition" mottaker={mottaker} className="hd-ambition hd-light">
    <Room700 />
    <p className="hd-room700-cap" data-testid="hd-room700-cap"><b>700</b> decision-makers. One room.</p>
    <p className="hd-statement" data-testid="hd-statement">
      <span className="dim">Not another conference.</span>
      One room where Norway's leaders set the direction on AI.
    </p>
  </Frame>
);

const WHY = [
  ["Decisions, not demos", "Leaders are now expected to decide on AI — budgets, risk, workforce, regulation — not just to understand it."],
  ["The room does not exist yet", "The conversation happens in London, Paris and San Francisco. Norway has the companies and the capital, but not the room."],
  ["The window is 2027", "The choices boards make in the next eighteen months set their trajectory for a decade. Timing is the point."],
];

/* Skiftet som tidslinje: tre stopp fra IT-avdelingen (grått) til styrerommet (blekk, gul markør) */
const SHIFT = [
  ["2019 – 2023", "The IT department", "Pilots, proofs of concept, a few enthusiasts."],
  ["2024 – 2026", "The budget line", "Real spend, real risk — and the first regulation, as the EU AI Act reaches Norway through the EEA."],
  ["2027", "The board agenda", "Strategy, workforce, capital. The year the decisions are made — and the year the room meets."],
];

export const WhyNow = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Why now" mottaker={mottaker} className="hd-why hd-light">
    <h2 className="hd-h" data-testid="hd-why-h">AI has moved from the IT department to the board agenda.</h2>
    <p className="hd-why-lede" data-testid="hd-why-lede">Three things have happened at once — and together they set the timing.</p>
    <ol className="hd-why-ledger" data-testid="hd-why-ledger">
      {WHY.map(([h, p], i) => (
        <li key={h}><span className="k">0{i + 1}</span><span className="t">{h}</span><span className="d">{p}</span></li>
      ))}
    </ol>
    <ol className="hd-shift" data-testid="hd-shift">
      {SHIFT.map(([y, t, d], i) => (
        <li key={y} className={i === SHIFT.length - 1 ? "now" : ""}><span className="y">{y}</span><i className="pt" /><span className="t">{t}</span><span className="d">{d}</span></li>
      ))}
    </ol>
  </Frame>
);

/* 04 Why Bergen — posisjonering, ikke vertsby. «AI capital» brukes ikke; ambisjonen sies én gang. */
const BERGEN = [
  ["Industry", "Maritime · energy · finance · media · technology"],
  ["Capital", "Owners, investors and institutions with national reach"],
  ["Talent", "Research, universities, founders and technical communities"],
  ["Momentum", "One annual national summit that brings the ecosystem into one room"],
];

export const Bergen = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Why Bergen" mottaker={mottaker} className="hd-bergen hd-light">
    <Img className="hd-bergen-img" src="/images/bergen-night.webp" alt="Bergen at night, seen from Fløyen" draggable={false} data-testid="hd-bergen-img" />
    <span className="hd-credit">Photo · Svein-Magne Tunli · CC BY-SA 4.0</span>
    <h2 className="hd-h2" data-testid="hd-bergen-h">Built in Bergen.<br />Designed for Norway.</h2>
    <p className="hd-bergen-sub">A city with global industries, capital, talent and the ambition to become a national centre for AI.</p>
    <ul className="hd-ledger" data-testid="hd-bergen-points">
      {BERGEN.map(([k, v]) => (
        <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>
      ))}
    </ul>
    <p className="hd-bergen-foot">Not in Bergen because it is convenient. Built here because Bergen can become one of Norway's most important AI cities.</p>
  </Frame>
);

const ROOM = ["CEOs and board members", "Investors and owners", "Founders and builders", "Technology leaders", "Public-sector leaders", "Researchers and policymakers"];

/* 04 The room — tallet er sliden; 700 punkter (ett per sete) i mediekolonnen, hvem som sitter der under */
export const Room = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The room" mottaker={mottaker} className="hd-room hd-light">
    <div className="hd-big" data-testid="hd-room-700">700</div>
    <p className="hd-sub">Seats. Curated for the people deciding<br />how AI is built, bought and governed.</p>
    <p className="hd-room-line" data-testid="hd-room-line">Invitation-led · One room, one programme · Grieghallen, Bergen · 13 April 2027</p>
    <Room700 />
    <p className="hd-room700-cap hd-room-cap" data-testid="hd-room700-cap">One dot per seat. <b>700</b> — not 7,000. The room is the product.</p>
    <div className="hd-side">
      <span className="k">Who is in the room</span>
      <ul>{ROOM.map((r) => <li key={r}>{r}</li>)}</ul>
    </div>
  </Frame>
);

const BLOKKER = [
  ["45–60 min", "Opening keynote", "Main stage, 09:30 — the opening session, presented by the Headliner Partner. The public moment, and the marketing engine for the summit."],
  ["20–30 min", "The Conversation", "Kevin O'Leary and the Headliner Partner's CEO in two chairs, moderated by the summit. The partner in the seat next to the headliner."],
  ["30–45 min", "The Executive Room", "15–20 invited leaders, closed room. The room only the Headliner Partner can open."],
  ["Remaining", "Press · photography · transitions", "Controlled access, partner content and buffer."],
];

const Headliner = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The headliner" mottaker={mottaker} className="hd-headliner hd-light">
    <div className="hd-photo">
      <Img src="/images/headliner/kevin-src-hi.webp" alt="Kevin O'Leary" draggable={false} />
    </div>
    <div className="hd-body">
      <h2 className="hd-name" data-testid="hd-headliner-name">Kevin O'Leary</h2>
      <p className="hd-lead">Proposed opening keynote. 2.5 hours on-site — designed across stage, conversation and executive access.</p>
      <div className="hd-blocks" data-testid="hd-blocks">
        {BLOKKER.map(([t, l, d]) => (
          <div className="hd-block" key={l}>
            <span className="t">{t}</span>
            <span className="l">{l}</span>
            <span className="d">{d}</span>
          </div>
        ))}
      </div>
      <p className="hd-line" data-testid="hd-onsite">
        <span className="gul">Availability confirmed</span> for 13 April 2027 — date confirmed available with his team; not yet contracted.
      </p>
    </div>
  </Frame>
);

/* The stage — én headliner + kuratert program. Statuser: kun det vi kan stå inne for (Tangen/Strümke ikke kontaktet). */
const PROGRAM = [
  ["Silvija Seres", "Technologist · Investor", "/speakers/seres.webp", "Availability confirmed", true, "seres"],
  ["Inga Strümke", "AI researcher · Author", "/speakers/strumke.webp", "Target", false, "strumke"],
  ["Nicolai Tangen", "CEO · Norges Bank Investment Management", "/speakers/tangen.webp", "Target", false, "tangen"],
  ["To be announced", "Frontier technology", null, "TBA", false, "tba"],
  ["To be announced", "Policy", null, "TBA", false, "tba"],
];

export const Stage = ({ nr, mottaker, partnerName }) => (
  <Frame nr={nr} label="The stage" mottaker={mottaker} className="hd-stage-s hd-light">
    <h2 className="hd-h2" data-testid="hd-stage-h">One headliner.<br />A stage built for substance.</h2>
    <figure className="hd-head" data-testid="hd-stage-headliner">
      <div className="ph"><Img src="/images/headliner/kevin-src-hi.webp" alt="Kevin O'Leary" draggable={false} /></div>
      <figcaption>
        <span className="n">Kevin O'Leary</span>
        <span className="r">Investor · Business</span>
        <span className="st on">Proposed headliner · Availability confirmed</span>
      </figcaption>
    </figure>
    <div className="hd-prog" data-testid="hd-people">
      <span className="k">The programme</span>
      <div className="row">
        {PROGRAM.map(([n, r, img, st, on, cls]) => (
          <figure className={`hd-person ${img ? "" : "tba"}`} key={n + r} data-testid={img ? undefined : "hd-tba-card"}>
            <div className="ph">{img ? <Img src={img} alt={n} draggable={false} className={cls} /> : <span className="tbamark" aria-hidden />}</div>
            <figcaption>
              <span className="n">{n}</span>
              <span className="r">{r}</span>
              <span className={`st ${on ? "on" : ""}`}>{st}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
    <div className="hd-stage-format" data-testid="hd-stage-format">
      <span className="k">09:30 · The opening session</span>
      <p>Opening keynote by Kevin O'Leary — followed by <b>The Conversation</b>: Kevin O'Leary and the CEO of <b>{partnerName || mottaker || "the Headliner Partner"}</b> in two chairs, moderated by the summit. The whole room in the same seat, from the first minute of the day.</p>
    </div>
    <p className="hd-voices">Business leadership · AI research · Frontier technology · Capital · Policy</p>
  </Frame>
);

/* The Conversation — kronjuvelen: intellektuelt eierskap, ikke scenebranding */
const CONV = [
  ["Who funds the AI transition?", "Capital allocation, risk appetite and what boards should expect before the money moves."],
  ["What should boards demand before deploying AI?", "Governance, security and the questions a chair asks — before the CIO answers."],
  ["Where does Norway actually have an advantage?", "Energy, trust, institutions and a small market that can move fast."],
  ["What changes first?", "Workforce, productivity or the balance sheet — and how a leader tells the difference."],
];

const Conversation = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The Conversation" mottaker={mottaker} className="hd-exec hd-conv hd-light">
    <h2 className="hd-h2 big" data-testid="hd-conv-h">The Conversation.</h2>
    <p className="hd-exec-sub" data-testid="hd-conv-sub"><b>Kevin O'Leary × Norwegian business leadership.</b> 20–30 minutes. One Norwegian agenda. No sales pitch. Themes are agreed in advance — with the partner and with the headliner — and the summit moderates.</p>
    <div className="hd-exec-grid" data-testid="hd-conv-points">
      {CONV.map(([t, d]) => (
        <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>
      ))}
    </div>
    <p className="hd-exec-foot">Presented with the Headliner Partner · Editorially produced by Norwegian AI Summit</p>
  </Frame>
);

/* How the stage is run — redaksjonell kontroll og brand safety: det usagte nei-argumentet i partnerens styrerom */
const STAGE_RULES = [
  ["Editorial control", "The summit owns the programme, the moderation and the brief. Every session is prepared — nothing is improvised on the partner's account."],
  ["The brief", "Themes and questions for the keynote and The Conversation are agreed in advance — with the partner and with the headliner."],
  ["Your voice", "The partner speaks for itself, on stage and in the room. The headliner speaks for himself. No one stands behind anyone else's opinions."],
  ["Your name", "The partner approves every use of its name and logo — and holds recording rights to its own moments."],
];

const StageRun = ({ nr, mottaker }) => (
  <Frame nr={nr} label="How the stage is run" mottaker={mottaker} className="hd-exec hd-light">
    <h2 className="hd-h2 big" data-testid="hd-run-h">A global headliner.<br />A Norwegian conversation.</h2>
    <p className="hd-exec-sub">The headliner brings the perspective and the attention. The Headliner Partner helps define what the conversation is about. The rules for how the stage is run are what make it safe to stand behind — for a bank, an energy company or a board.</p>
    <div className="hd-exec-grid" data-testid="hd-run-points">
      {STAGE_RULES.map(([t, d]) => (
        <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>
      ))}
    </div>
    <p className="hd-exec-foot">Prepared, moderated, approved — the partner is never surprised by its own stage.</p>
  </Frame>
);

/* The day — designet dagsreise */
const DAG = [
  ["09:00", "Arrival"], ["09:30", "Opening keynote · Kevin O'Leary (proposed)", true], ["10:20", "The Conversation", true], ["11:00", "The Executive Room", true],
  ["11:30", "Leadership programme"], ["13:00", "AI Experience"], ["16:00", "Closing keynote"], ["18:30", "Dinner · Spissen"],
];

export const Day = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The day" mottaker={mottaker} className="hd-day hd-light">
    <h2 className="hd-h2" data-testid="hd-day-h">This is not a sequence of talks.<br />It is a designed day.</h2>
    <ol className="hd-timeline" data-testid="hd-timeline">
      {DAG.map(([t, l, hi]) => (
        <li key={t} className={hi ? "hi" : ""}>
          <span className="t">{t}</span>
          <i className="pt" />
          <span className="l">{l}</span>
        </li>
      ))}
    </ol>
    <p className="hd-dayfoot">Highlighted: the headliner's 2.5 hours — keynote, The Conversation and the executive room — open the day, when the whole room is in the same seat. Times are proposed and set with the programme.</p>
  </Frame>
);

/* Beyond the stage — slik føles dagen mellom programpunktene (konseptbilder fra Vision-decket, tonet som The place) */
const BEYOND = [
  ["AI Experience", "See it. Test it. Meet the people building it.", "/images/vision-expo-v3.webp"],
  ["The network", "CEOs · founders · investors · researchers · decision-makers", "/images/vision-networking-v3.webp"],
  ["Summit Dinner", "The conversation continues after the stage closes.", "/images/vision-dinner-v4.webp"],
];

export const Beyond = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Beyond the stage" mottaker={mottaker} className="hd-beyond hd-light">
    <h2 className="hd-h2" data-testid="hd-beyond-h">The summit is bigger<br />than the stage.</h2>
    <div className="hd-frags" data-testid="hd-beyond-frags">
      {BEYOND.map(([n, d, img]) => (
        <figure className="hd-frag" key={n}>
          <div className="ph"><Img src={img} alt={n} draggable={false} /></div>
          <figcaption>
            <span className="n">{n}</span>
            <span className="d">{d}</span>
          </figcaption>
        </figure>
      ))}
    </div>
  </Frame>
);

/* Built for reach — ambisjon, ikke garanterte rekkeviddetall. Annonseringen er det første rekkevidde-øyeblikket. */
const REACH = [
  ["Announcement", "Announced together with the programme — the Headliner Partner is part of the news from day one"],
  ["Within 24 hours", "Flagship clips from the keynote and The Conversation — cut for the partner's channels and ours"],
  ["On demand", "Full sessions after the summit; the partner's moments as stand-alone cuts, cleared for its own use"],
  ["Through the year", "LinkedIn, YouTube and press assets — and a post-summit editorial series that keeps the partner's name in the conversation"],
];

export const Reach = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Built for reach" mottaker={mottaker} className="hd-reach hd-light">
    <h2 className="hd-h2" data-testid="hd-reach-h">Built for 700.<br />Designed to travel further.</h2>
    <ul className="hd-reach-list" data-testid="hd-reach-list">
      {REACH.map(([k, d]) => (
        <li key={k}><span className="k">{k}</span><span className="d">{d}</span></li>
      ))}
    </ul>
    <div className="hd-reach-ph"><Img src="/images/vision-broadcast.webp" alt="Broadcast production" draggable={false} /></div>
    <p className="hd-reach-foot">The distribution plan is built with the programme. Reach figures follow when they are real — we do not invent them.</p>
  </Frame>
);

/* What you cannot buy alone — svaret på «vi kan jo leie Grieghallen og booke ham selv» */
const CANT = [
  ["A room no single company can convene", "Leaders across industries, institutions and competing organisations come because the room is neutral. They do not come to a company event."],
  ["700 seats across sectors", "Curated by the summit across energy, finance, industry, the public sector and technology — not a client list."],
  ["The news moment", "The announcement and the day are national news. A company event is a press release."],
  ["The programme around you", "A frontier lab, a law firm, a build platform, research and capital on the same stage — a day you did not have to produce."],
];

const CannotBuy = ({ nr, mottaker }) => (
  <Frame nr={nr} label="What you cannot buy alone" mottaker={mottaker} className="hd-cant hd-light">
    <h2 className="hd-h2" data-testid="hd-cant-h">What you cannot<br />buy alone.</h2>
    <ul className="hd-ledger" data-testid="hd-cant-points">
      {CANT.map(([k, v]) => (
        <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>
      ))}
    </ul>
    <p className="hd-cant-foot">The Headliner Partner stands behind a national stage — without having to build one.</p>
  </Frame>
);

/* Built to deliver — appendix: leveransemodell, nøktern (Unite/Apriil er forslag/tilbud, ikke inngåtte avtaler) */
const LEVERANSE = [
  ["Norwegian AI Summit", null, "Vision · programme · partnerships · positioning", "Owner"],
  ["Unite Event", "/logos/unite.svg", "Project leadership · production · logistics · participant journey", "Proposed"],
  ["Grieghallen", "/logos/grieghallen.svg", "Venue · technical infrastructure · hospitality", "Offer received"],
  ["Apriil", "/logos/apriil.svg", "Brand identity · visual system · partner communications", "Proposed"],
];

export const Deliver = ({ nr, mottaker, label = "Appendix · Built to deliver" }) => (
  <Frame nr={nr} label={label} mottaker={mottaker} className="hd-deliver hd-light">
    <h2 className="hd-h2" data-testid="hd-deliver-h">Built to deliver.</h2>
    <p className="hd-deliver-sub">A delivery model developed with selected partners for production, venue and brand.</p>
    <div className="hd-team" data-testid="hd-team">
      {LEVERANSE.map(([n, logo, r, st]) => (
        <div className="hd-actor" key={n}>
          <span className="lg" data-testid={`hd-logo-${n.toLowerCase().split(" ")[0]}`}>
            {logo ? (
              <Img src={logo} alt={n} draggable={false} className={n.toLowerCase().split(" ")[0]} />
            ) : (
              <span className="wordmark hd-wm-l"><span className="wordmark__bold">Norwegian</span><span className="wordmark__light">AI Summit</span></span>
            )}
          </span>
          <span className="r">{r}</span>
          <span className="st">{st}</span>
        </div>
      ))}
    </div>
    <p className="hd-deliver-key" data-testid="hd-deliver-key"><b>Confirmed</b> — contracted · <b>Offer received</b> — written offer, not yet signed · <b>Proposed</b> — in discussion, not yet agreed · <b>Availability confirmed</b> — date held, not yet contracted</p>
    <p className="hd-deliver-foot">Headliner: Kevin O'Leary — availability confirmed for 13 April 2027, not yet contracted. One integrated team from programme and partnerships to production and delivery; nothing above is presented as signed unless it is.</p>
  </Frame>
);

/* The economics — lyst oppslag, investment memo */
const ECON = [
  ["Venue & production", 24], ["International speakers", 35], ["Experience & broadcast", 10], ["Operations & marketing", 22],
];

export const Economics = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The economics" mottaker={mottaker} className="hd-econ hd-light">
    <div className="hd-econ-l">
      <div className="hd-sum" data-testid="hd-econ-sum">9–10 <span className="u">MNOK</span></div>
      <p className="hd-sumlab">Total event investment</p>
      <p className="hd-econ-thesis">Tickets fund the room.<br />Partners elevate the stage.</p>
    </div>
    <div className="hd-econ-r" data-testid="hd-econ-rows">
      {ECON.map(([l, w]) => (
        <div className="hd-erow" key={l}>
          <span className="l">{l}</span>
          <span className="bar"><i style={{ width: `${(w / 35) * 100}%` }} /></span>
        </div>
      ))}
      <p className="hd-econ-note">Headliner investment creates the difference between a good event and a category-defining one.</p>
    </div>
  </Frame>
);

/* One headliner. One Headliner Partner. — scarcity/arkitektur, kategoritanken eksplisitt */
const KNAPPHET = [
  "One category-exclusive Headliner Partner",
  "Presented-by position around the keynote",
  "Priority access to the closed-door executive session",
  "Integrated presence across stage, content and broadcast",
];
const UNIVERS = ["Founding", "Headliner", "Frontier", "Build", "AI Law & Policy", "Dinner", "Broadcast"];

export const OnePartner = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The position" mottaker={mottaker} className="hd-one hd-light">
    <h2 className="hd-one-h" data-testid="hd-one-h">One headliner.<br />One Headliner Partner.</h2>
    <p className="hd-one-sub">One partner owns the commercial position around the summit's international headliner — with category exclusivity.</p>
    <p className="hd-one-line" data-testid="hd-one-line">One Headliner Partner · one category · no competing position.</p>
    <ul className="hd-one-pts" data-testid="hd-one-points">
      {KNAPPHET.map((k) => <li key={k}>{k}</li>)}
    </ul>
    <p className="hd-one-univ" data-testid="hd-one-universe">
      {UNIVERS.map((u, i) => (
        <span key={u} className={u === "Headliner" ? "on" : ""}>{u}{i < UNIVERS.length - 1 && <i> · </i>}</span>
      ))}
    </p>
  </Frame>
);

/* Why <mottaker> — vises kun når decket er personalisert. Kategori styrer argumentene; kjente mottakere får en egen første linje. */
const WHY_FIRST = {
  dnb: "Norway's largest financial group belongs next to the country's most ambitious AI moment.",
  "sparebanken norge": "Headquartered in Bergen — the summit's home city — and newly named for the whole country. The first national stage to carry the name Sparebanken Norge should be built here.",
  telenor: "The company that connects Norway, at the centre of the conversation about what runs on that connection next.",
  equinor: "Energy is where Norway's AI decisions become physical. The country's largest company should be in that room as a host, not a guest.",
};
const WHY_CATEGORY = {
  banking: (m) => [
    ["Capital", `${m} sits where Norwegian companies decide what to fund and scale. AI adoption is capital allocation — and the bank is in the room where it happens.`],
    ["Trust", "AI adoption in Norway will depend on security, governance and confidence. That is the bank's home ground — and the room's first question."],
    ["Business transformation", `Few organisations touch more of Norwegian business — from founders to the country's largest companies. ${m} at the centre of the AI conversation is the national position, not a sponsorship.`],
  ],
  energy: (m) => [
    ["Where AI becomes physical", `Energy is where models meet turbines, grids and safety. ${m} owning the headliner position frames AI as an industrial question.`],
    ["The board-level audience", "700 decision-makers — customers, suppliers, regulators and capital in one room."],
    ["One energy partner. No competitor.", `Category exclusivity: ${m} is the only energy company in the headliner programme.`],
  ],
  telecom: (m) => [
    ["The infrastructure of AI", `Everything on stage runs on connectivity. ${m} as Headliner Partner makes that visible to the people who decide.`],
    ["Enterprise buyers in the room", "700 leaders — the enterprise customers whose AI ambitions become network and cloud decisions."],
    ["One telecom partner. No competitor.", `Category exclusivity: ${m} is the only telecom company in the headliner programme.`],
  ],
  insurance: (m) => [
    ["Risk is the AI question", `Boards now price AI as risk. ${m} at the centre of that conversation is category leadership, not sponsorship.`],
    ["Your customers are in the room", "700 CEOs, board members and owners — the corporate clients whose exposure you underwrite."],
    ["One insurer. No competitor.", `Category exclusivity: ${m} is the only insurer in the headliner programme.`],
  ],
  consulting: (m) => [
    ["Where the mandates are decided", `The room is 700 leaders deciding what to do about AI. ${m} owning the headliner position is presence at the decision, not after it.`],
    ["Authority, not a booth", "The Headliner Partner stands behind the headliner and the closed room — the most senior position of the day."],
    ["One advisory partner. No competitor.", `Category exclusivity: ${m} is the only advisory firm in the headliner programme.`],
  ],
  technology: (m) => [
    ["The category leader's seat", `The summit is where Norway's leaders set direction on AI. ${m} as Headliner Partner is the name that direction is set with.`],
    ["Buyers, not developers", "700 decision-makers — the executives who sign, not the teams who evaluate."],
    ["One technology partner. No competitor.", `Category exclusivity: ${m} is the only technology company in the headliner programme.`],
  ],
};
const WHY_GENERIC = (m) => [
  ["Category leadership", `The Headliner Partner is the name the summit is built with. ${m} holding it says who leads the category on AI.`],
  ["The right 700", "CEOs, board members, founders, investors and public-sector leaders — the people whose decisions shape your market."],
  ["Capital, trust, leadership", `The AI economy needs all three. ${m} at the centre of the national conversation is a strategic position, not a sponsorship.`],
];
/* Kjente mottakere med egen, 100 % personalisert argumentasjon (offentlig kjent strategi — ingen interne tall) */
const WHY_RECIPIENT = {
  "sparebanken norge": [
    ["A national bank being built now", "A newly formed national bank, headquartered in Bergen — the summit's home city — with ambitions beyond its traditional regions. The first national stage to carry the name Sparebanken Norge should be built here."],
    ["AI on the strategic agenda", "Technology modernisation and AI are already part of the bank's transformation. This is the room where that agenda meets the rest of Norwegian business."],
    ["Capital meets the real economy", "The bank sits between Norwegian businesses, owners and the capital needed to transform. AI adoption is capital allocation — and the bank is where it happens."],
    ["Trust at scale", "Few institutions have a stronger reason — or a better position — to shape how AI is adopted responsibly in Norway."],
  ],
};
const WHY_FOOT = {
  "sparebanken norge": "The Headliner Partnership puts Sparebanken Norge behind the conversation — not behind another logo wall.",
};
const whyFor = (m, category) => {
  const own = WHY_RECIPIENT[m.toLowerCase()];
  if (own) return own;
  const pts = (WHY_CATEGORY[(category || "").toLowerCase()] || WHY_GENERIC)(m);
  const first = WHY_FIRST[m.toLowerCase()];
  return first ? [[`Why ${m}`, first], ...pts] : pts;
};

const WhyYou = ({ nr, mottaker, category }) => (
  <Frame nr={nr} label={`Why ${mottaker}`} mottaker={mottaker} className="hd-whyyou hd-light">
    <h2 className="hd-whyyou-h" data-testid="hd-whyyou-h">Why {mottaker}.</h2>
    <ul className="hd-whyyou-ledger" data-testid="hd-whyyou-points">
      {whyFor(mottaker, category).map(([k, v]) => (
        <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>
      ))}
    </ul>
    <LogoStamp name={mottaker} sub={`Headliner Partner · Norwegian AI Summit 2027${category ? ` · ${category}` : ""}`} />
    <p className="hd-whyyou-foot" data-testid="hd-whyyou-foot">{WHY_FOOT[mottaker.toLowerCase()] || `That makes ${mottaker} the natural Headliner Partner.`}</p>
  </Frame>
);

/* Your position — tre eiendeler (brukerens review: enklere å selge internt enn åtte rettigheter): the stage · the room · the content */
const ASSETS3 = [
  ["The stage", "700 leaders", ["Opening keynote — presented by the Headliner Partner", "The Conversation with Kevin O'Leary", "Brand presence across the summit"]],
  ["The room", "15–20 private executive guests", ["The Executive Room — partner hosted, guest list built together", "50 seats · 100 nominated invitations", "Summit Dinner · a table of 10"]],
  ["The content", "National reach beyond the day", ["Content, broadcast & recording rights", "The announcement — together with the programme", "Partner report after the summit"]],
];

const Position = ({ nr, mottaker, category }) => (
  <Frame nr={nr} label="Your position" mottaker={mottaker} className="hd-pos hd-light">
    <div className="hd-pos-l">
      <span className="k">The Headliner Partner</span>
      <h2 className="hd-h2" data-testid="hd-pos-h">One position.<br />Three assets.</h2>
      <p className="hd-pos-p">Owned, not sponsored. The commercial leadership position of Norwegian AI Summit — a single partner stands behind the headliner, the closed room and the most visible moments of the day. Not a logo among many, but the name the summit is built with.</p>
      <p className="hd-pos-cat" data-testid="hd-pos-category">Two exclusivities: the only Headliner Partner — {category ? `and the only ${CATEGORY_NOUN[category] || category} in the partner programme.` : "and no competitor in the partner's category."}</p>
      <p className="hd-one-univ hd-pos-univ" data-testid="hd-pos-universe">
        {UNIVERS.map((u, i) => (
          <span key={u} className={u === "Headliner" ? "on" : ""}>{u}{i < UNIVERS.length - 1 && <i> · </i>}</span>
        ))}
      </p>
    </div>
    <ol className="hd-assets" data-testid="hd-rights">
      {ASSETS3.map(([t, big, items], i) => (
        <li key={t}><span className="i">0{i + 1}</span><span className="t">{t}</span><span className="big">{big}</span><ul>{items.map((x) => <li key={x}>{x}</li>)}</ul></li>
      ))}
    </ol>
  </Frame>
);

/* The thesis — hvorfor en partner overhodet: kapital, tillit, lederskap (før Why <mottaker>) */
const THESIS = [
  ["Capital", "decides how fast Norway adopts AI — what is funded, scaled and stopped."],
  ["Trust", "decides whether it is adopted at all — security, governance, confidence."],
  ["Leadership", "decides who sets the direction — in the boardroom and on the national stage."],
];

const Thesis = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The position" mottaker={mottaker} className="hd-thesis hd-light">
    <h2 className="hd-h2 bd-h80" data-testid="hd-thesis-h">The AI economy needs<br />more than technology.</h2>
    <p className="hd-thesis-sub" data-testid="hd-thesis-sub">It needs capital, trust and leadership. The Headliner Partner is the institution that brings all three into the room — and Kevin O'Leary is the international catalyst inside that position, not the position itself.</p>
    <ul className="hd-thesis-pts" data-testid="hd-thesis-points">
      {THESIS.map(([k, v]) => <li key={k}><b>{k}</b> {v}</li>)}
    </ul>
  </Frame>
);

/* The Executive Room — mest eksklusiv partner-slide, ren typografi */
const ROM = [
  ["Invitation only", "15–20 at CEO, Chair, board and owner level — guest list built with the partner, curated by the summit"],
  ["Closed room", "No press, no recording, no sales presentation. Chatham House Rule"],
  ["Partner hosted", "The Headliner Partner hosts the conversation, Kevin O'Leary is in the room, the summit moderates"],
  ["30–45 minutes", "Conversation, not presentation — placed in the programme after the opening session"],
];

const ExecRoom = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The Executive Room" mottaker={mottaker} className="hd-exec hd-light">
    <h2 className="hd-h2 big" data-testid="hd-exec-h">15–20 leaders.<br />One private conversation.</h2>
    <p className="hd-exec-sub">A private 30–45 minute conversation with Kevin O'Leary and a curated group of executives, customers, owners and decision-makers — selected together with the Headliner Partner.</p>
    <div className="hd-exec-grid" data-testid="hd-exec-points">
      {ROM.map(([t, d]) => (
        <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>
      ))}
    </div>
    <p className="hd-exec-foot">No public audience · Controlled access · Photography afterwards</p>
  </Frame>
);

/* The place — ett ekte Grieghallen-foto, nesten ingen tekst */
export const Place = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The place" mottaker={mottaker} className="hd-place">
    <Img className="hd-place-img" src="/images/grieghallen.webp" alt="Grieghallen, Bergen" draggable={false} />
    <div className="hd-place-shade" aria-hidden />
    <div className="hd-place-txt">
      <h2 data-testid="hd-place-h">Grieghallen. Bergen.</h2>
      <p className="d">13 April 2027</p>
      <p className="s">One day. One room. 700 decision-makers.</p>
    </div>
  </Frame>
);

/* On one page — det partneren sender videre internt */
const ONEPAGE = (m, c) => [
  ["What", `${m || "One company"} as the Headliner Partner of Norwegian AI Summit 2027 — the position around the international headliner, ${c ? `exclusive in ${c}` : "category exclusive"}.`],
  ["Why", `${m || "The Headliner Partner"} convenes a national conversation about capital, trust and leadership in the AI economy — with Kevin O'Leary (proposed opening keynote) as the international catalyst. One room, 700 of Norway's leaders.`],
  ["You get", "Opening keynote presented by · The Conversation with Kevin O'Leary · The Executive Room · 50 seats and 100 nominated invitations · Summit Dinner · content, broadcast and recording rights · partner report."],
  ["Optional", "Seats for your own technology and leadership talent · a hosted client dinner the evening before · The Executive Room as a client breakfast."],
  ["Investment", "One fee. Two exclusivities: the only Headliner Partner, and the only company in your category in the partner programme. Commercial terms discussed directly."],
  ["Timeline", "Position confirmed by 1 October 2026 · designed together Q4 2026 · announced with the programme."],
];

const OnePage = ({ nr, mottaker, category }) => (
  <Frame nr={nr} label="On one page" mottaker={mottaker} className="lw-onepage hd-light">
    <h2 className="hd-h2 bd-h80" data-testid="hd-onepage-h">On one page.</h2>
    <dl className="lw-onepage-grid" data-testid="hd-onepage">
      {ONEPAGE(mottaker, category).map(([k, v]) => (
        <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
      ))}
    </dl>
  </Frame>
);

/* Next step — prosess, frist og signatur */
const STEPS = [
  ["01", "Conversation", "A 45-minute meeting on the position and the partner's ambitions."],
  ["02", "Term sheet", "One page: position, deliverables, fee, both exclusivities — and data, content, recording and headliner-replacement terms."],
  ["03", "Design together", "The Conversation, the Executive Room, the guest list and the announcement — Q4 2026."],
  ["04", "Announcement", "Announced together with the programme — the Headliner Partner is part of the news."],
];

const Next = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Next step" mottaker={mottaker} className="hd-next lw-next hd-light">
    <h2 className="hd-next-h" data-testid="hd-next-h">One Headliner Partnership.</h2>
    <p className="hd-next-p" data-testid="hd-next-p">
      Kevin O'Leary's availability is confirmed for Bergen, 13 April 2027 — not yet contracted.<br />
      {mottaker ? `We would like to explore ${mottaker} as the Headliner Partner.` : "We are selecting one category-exclusive partner for the headliner programme."}
    </p>
    <ol className="lw-steps" data-testid="hd-steps">
      {STEPS.map(([n, t, d]) => (
        <li key={n}><span className="n">{n}</span><span className="t">{t}</span><span className="d">{d}</span></li>
      ))}
    </ol>
    <p className="hd-next-terms lw-deadline" data-testid="hd-deadline"><span className="k">Decision</span>Position confirmed by <b>1 October 2026</b> · designed together Q4 2026 · announced with the programme.</p>
    <div className="hd-contact" data-testid="hd-contact">
      <span className="n">Martin · Founder, Norwegian AI Summit</span>
      <span className="e">martin@norwegianaisummit.no</span>
    </div>
  </Frame>
);

const CATEGORY_NOUN = { banking: "bank", energy: "energy company", telecom: "telecom operator", insurance: "insurer", consulting: "advisory firm", technology: "technology company" };

const HdProposal = ({ nr, mottaker, category }) => (
  <Proposal
    nr={nr} mottaker={mottaker}
    sentence={<><b>{mottaker}</b> as the Headliner Partner of Norwegian AI Summit 2027 — the name the national AI stage is built with.</>}
    index={[
      ["Opening keynote", `Presented by ${mottaker} · 09:30`],
      ["Announced", "With the programme · together"],
      ["Position", `One ${CATEGORY_NOUN[category] || "partner"}. Category exclusive.`],
    ]}
  />
);

/* Dramaturgi: Built to deliver ligger som appendix etter Next step (due diligence, ikke salgsargument) */
/* Kuttet etter gjennomgang (23 → 18): The ambition, The stage, The place, Beyond, One partner (universlinjen ligger på Position), Economics.
   Why Bergen beholdt (gjennomgangen: «absolutt behold»). Built to deliver før On one page; decket slutter på «The proposal». */
/* Dramaturgi (brukerens review fra konsernsjef-stolen): what → 700 → why now → capital/trust/leadership → [Why deg] → Kevin →
   The Conversation → how the stage is run → The Executive Room → the day → Bergen → reach → cannot buy → position → deliver → one page → next */
const SLIDES = [Cover, WhatItIs, Room, WhyNow, Thesis, Headliner, Conversation, StageRun, ExecRoom, Day, Bergen, Reach, CannotBuy, Position, Deliver, OnePage, Next];
const slidesFor = (mottaker) => (mottaker ? [...SLIDES.slice(0, 5), WhyYou, ...SLIDES.slice(5), HdProposal] : SLIDES);

/* Alle bilder i decket — forhåndslastes av vieweren så slide-bytter er umiddelbare */
const ASSETS = [
  ...CINE_ASSETS, "/images/headliner/kevin-src-hi.webp", "/images/bergen-night.webp",
  "/images/vision-expo-v3.webp", "/images/vision-networking-v3.webp", "/images/vision-dinner-v4.webp", "/images/vision-broadcast.webp",
  "/speakers/seres.webp", "/speakers/strumke.webp", "/speakers/tangen.webp",
  "/logos/unite.svg", "/logos/grieghallen.svg", "/logos/apriil.svg",
];

export function HeadlinerPrint() {
  return <DeckPrint slides={slidesFor} fallback="" />;
}

/* Generisk som standard (ingen «Prepared for») — ?for=<mottaker> personaliserer. publicMode skjuler redigeringsfeltene. */
export default function HeadlinerDeck({ mottaker = "", publicMode = false }) {
  return <DeckViewer deck="headliner" slides={slidesFor} fallback={mottaker} filePrefix="Norwegian-AI-Summit-Headliner-Partnership" assets={ASSETS} publicMode={publicMode} />;
}
